import { useEffect, useState } from "react";
import MainLayout from "@/components/layout/MainLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Settings, Save, Info, ArrowLeft, Bell } from "lucide-react";
import { Link } from "react-router-dom";
import { extractErrorMessage } from "@/lib/errorMessage";

const STAGES = [
  { id: "contato_cliente", label: "Contato com Cliente", color: "#3B82F6" },
  { id: "visita_tecnica", label: "Visita Tecnica", color: "#F59E0B" },
  { id: "propostas", label: "Propostas", color: "#10B981" },
  { id: "diagnostico", label: "Proposta Potencial", color: "#8B5CF6" },
];

export default function CRMConfigPage() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [slaAmareloGlobal, setSlaAmareloGlobal] = useState(2);
  const [slaVermelhoGlobal, setSlaVermelhoGlobal] = useState(5);
  const [slaPorEtapa, setSlaPorEtapa] = useState({});
  const [browserNotifGranted, setBrowserNotifGranted] = useState(false);

  const token = sessionStorage.getItem("luvcorp_token");
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    if (typeof window !== "undefined" && "Notification" in window) {
      setBrowserNotifGranted(Notification.permission === "granted");
    }
    axios.get(`${API}/crm/settings`, { headers })
      .then(r => {
        setSlaAmareloGlobal(r.data.sla_amarelo_global || 2);
        setSlaVermelhoGlobal(r.data.sla_vermelho_global || 5);
        setSlaPorEtapa(r.data.sla_por_etapa || {});
      })
      .catch(() => toast.error("Erro ao carregar configuracoes"))
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    if (slaAmareloGlobal >= slaVermelhoGlobal) {
      toast.error("SLA amarelo deve ser menor que vermelho");
      return;
    }
    setSaving(true);
    try {
      await axios.put(`${API}/crm/settings`, {
        sla_amarelo_global: parseInt(slaAmareloGlobal),
        sla_vermelho_global: parseInt(slaVermelhoGlobal),
        sla_por_etapa: slaPorEtapa,
      }, { headers });
      toast.success("Configuracoes salvas!");
    } catch (err) {
      toast.error(extractErrorMessage(err, "Erro ao salvar"));
    } finally {
      setSaving(false);
    }
  };

  const handleEtapaChange = (etapaId, campo, valor) => {
    setSlaPorEtapa(prev => ({
      ...prev,
      [etapaId]: { ...prev[etapaId], [campo]: parseInt(valor) || 0 },
    }));
  };

  const handleRemoveEtapa = (etapaId) => {
    setSlaPorEtapa(prev => {
      const next = { ...prev };
      delete next[etapaId];
      return next;
    });
  };

  const requestBrowserNotif = async () => {
    if (!("Notification" in window)) {
      toast.error("Seu navegador nao suporta notificacoes");
      return;
    }
    const perm = await Notification.requestPermission();
    setBrowserNotifGranted(perm === "granted");
    if (perm === "granted") {
      new Notification("Obra Padrão", { body: "Notificações do navegador ativadas!", icon: "/brand/obrapadrao-logo.jpeg" });
      toast.success("Notificacoes ativadas no navegador");
    } else {
      toast.error("Permissao negada");
    }
  };

  if (loading) return <MainLayout><div className="flex items-center justify-center h-64"><Loader2 className="h-8 w-8 animate-spin text-[#0A1628]" /></div></MainLayout>;

  return (
    <MainLayout>
      <div className="space-y-6 max-w-3xl" data-testid="crm-config-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-heading font-bold text-[#0A1628] flex items-center gap-2"><Settings className="h-6 w-6" />Configuracoes do CRM</h1>
            <p className="text-sm text-slate-500 mt-1">Personalize SLA por etapa e alertas do navegador.</p>
          </div>
          <Link to="/comercial/crm"><Button variant="outline" size="sm"><ArrowLeft className="h-4 w-4 mr-1" />Voltar</Button></Link>
        </div>

        {/* Browser Notifications */}
        <Card className="border-0 shadow-sm" data-testid="card-browser-notif">
          <CardHeader>
            <CardTitle className="text-base text-[#0A1628] flex items-center gap-2"><Bell className="h-5 w-5" />Notificacoes do Navegador</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-sm text-slate-600">
              Permita que o sistema mostre alertas pop-up nativos do navegador quando voce receber uma notificacao
              importante (lead atrasado, SLA estourado, mudanca de etapa). Funciona mesmo em outras abas.
            </p>
            {browserNotifGranted ? (
              <Badge className="bg-emerald-100 text-emerald-700" data-testid="badge-notif-ativadas">Ativadas</Badge>
            ) : (
              <Button onClick={requestBrowserNotif} className="bg-[#0A1628]" size="sm" data-testid="btn-ativar-notif-browser">
                <Bell className="h-4 w-4 mr-1" />Ativar Notificacoes do Navegador
              </Button>
            )}
          </CardContent>
        </Card>

        {/* SLA Global */}
        <Card className="border-0 shadow-sm" data-testid="card-sla-global">
          <CardHeader>
            <CardTitle className="text-base text-[#0A1628]">SLA Global (padrao para todas as etapas)</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <div className="h-3 w-3 rounded-full bg-amber-500" /> Dias para alerta amarelo
                </Label>
                <Input type="number" min="1" value={slaAmareloGlobal} onChange={e => setSlaAmareloGlobal(e.target.value)} data-testid="input-sla-amarelo-global" />
              </div>
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <div className="h-3 w-3 rounded-full bg-red-500" /> Dias para alerta vermelho
                </Label>
                <Input type="number" min="2" value={slaVermelhoGlobal} onChange={e => setSlaVermelhoGlobal(e.target.value)} data-testid="input-sla-vermelho-global" />
              </div>
            </div>
            <div className="flex items-start gap-2 p-3 bg-blue-50 rounded text-xs text-blue-700">
              <Info className="h-4 w-4 shrink-0 mt-0.5" />
              <p>Quando um lead fica parado pelo numero de dias acima, o card pulsa na cor correspondente no Kanban e o responsavel recebe uma notificacao automatica (com dedup diario).</p>
            </div>
          </CardContent>
        </Card>

        {/* SLA por Etapa */}
        <Card className="border-0 shadow-sm" data-testid="card-sla-por-etapa">
          <CardHeader>
            <CardTitle className="text-base text-[#0A1628]">SLA por Etapa (sobrescreve global)</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {STAGES.map(s => {
              const customizado = !!slaPorEtapa[s.id];
              return (
                <div key={s.id} className="flex items-center gap-3 p-3 rounded-lg border" data-testid={`etapa-row-${s.id}`}>
                  <div className="h-3 w-3 rounded-full shrink-0" style={{ backgroundColor: s.color }} />
                  <span className="text-sm font-medium flex-1" style={{ color: s.color }}>{s.label}</span>
                  {customizado ? (
                    <>
                      <Input type="number" min="1" value={slaPorEtapa[s.id]?.amarelo || ""} onChange={e => handleEtapaChange(s.id, "amarelo", e.target.value)} className="w-20 h-8" placeholder="amar." />
                      <Input type="number" min="2" value={slaPorEtapa[s.id]?.vermelho || ""} onChange={e => handleEtapaChange(s.id, "vermelho", e.target.value)} className="w-20 h-8" placeholder="verm." />
                      <Button variant="ghost" size="sm" onClick={() => handleRemoveEtapa(s.id)} className="text-red-600 text-xs h-8">Usar global</Button>
                    </>
                  ) : (
                    <Button variant="outline" size="sm" onClick={() => handleEtapaChange(s.id, "amarelo", slaAmareloGlobal)} className="text-xs h-8" data-testid={`btn-customizar-${s.id}`}>
                      Customizar
                    </Button>
                  )}
                </div>
              );
            })}
          </CardContent>
        </Card>

        <div className="flex justify-end">
          <Button onClick={handleSave} disabled={saving} className="bg-[#0A1628]" data-testid="btn-salvar-config">
            <Save className="h-4 w-4 mr-1" />{saving ? "Salvando..." : "Salvar configuracoes"}
          </Button>
        </div>
      </div>
    </MainLayout>
  );
}
